import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import useToken from "../Authentication/useToken";
import { useQuery } from "@tanstack/react-query";
import { Card, Button, Descriptions, DatePicker, Alert, Spin, Radio } from "antd";
import {
  XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend, ResponsiveContainer
} from "recharts";
import { useTranslation } from "react-i18next";
import Constants from "../Utils/Constants";
import "./Styles.css";
import PropTypes from "prop-types";
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import locale from 'antd/es/date-picker/locale/fr_FR';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import InfoCard from "./InfoCard";
import SuiviEvaluationsCard from "./SuiviEvaluationsCard";
import PatientFiltersCard from "./PatientFiltersCard";
import AllPatientsStats from "./AllPatientsStats";
import {
  buildClinicOptions,
  filterPatientsByClinic,
  resolveSelectedPatient,
  getReplacementSelectedPatientId,
  buildPatientStatsCsvRows,
  buildCsvFileName,
  getPaceStatusState,
} from "./PatientStats.helpers";

// Modele d'export: nombre de blocs "semaine" generes dans le CSV.
const CSV_WEEK_COUNT = 2;
// Valeur affichee quand une donnee de session est absente.
const CSV_MISSING_VALUE = "NA";


dayjs.extend(quarterOfYear);
dayjs.locale('fr');

const { RangePicker } = DatePicker;

function toCsvNumber(value) {
  // Accepte "12.5" et "12,5" pour gerer des formats mixtes.
  if (value == null || value === "") return null;
  const normalized = String(value).replace(",", ".").trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatCsvNumber(value) {
  if (!Number.isFinite(value)) return CSV_MISSING_VALUE;
  const rounded = Math.round(value * 100) / 100;
  return String(rounded);
}

function weekMetricHeaders(weekNumber) {
  // Conserver les en-tetes alignes avec le modele Excel partage.
  const week2 = String(weekNumber).padStart(2, "0");
  return [
    "seance1-nombre d'exercice",
    "seance2-nombre d'exercice",
    "seance3-nombre d'exercice",
    "seance4-nombre d'exercice",
    "seance5-nombre d'exercice",
    "seance6-nombre d'exercice",
    "seance7-nombre d'exercice",
    `Sem${weekNumber}_nombre totale exercices`,
    "seance1-difficulte",
    "seance2-difficulte",
    "seance3-difficulte",
    "seance4-difficulte",
    "seance5-difficulte",
    "seance6-difficulte",
    "seance7-difficulte",
    `C_Sem${weekNumber}_difficulte`,
    `sem${weekNumber}_diff_moy`,
    "seance1-douleur",
    "seance2-douleur",
    "seance3-douleur",
    "seance4-douleur",
    "seance5-douleur",
    "seance6-douleur",
    "seance7-douleur",
    `sem${weekNumber}_douleur`,
    `sem${weekNumber}_douleur_moy`,
    "seance1-Motivation",
    "seance2-Motivation",
    "seance3-Motivation",
    "seance4-Motivation",
    "seance5-Motivation",
    "seance6-Motivation",
    "seance7-Motivation",
    `sem${week2}_motiv`,
    `sem${weekNumber}_motiv_moy`,
    "jour 1-marche",
    "jour 2-marche",
    "jour 3-marche",
    "jour 4-marche",
    "jour 5-marche",
    "jour 6-marche",
    "jour 7-marche",
    `sem${weekNumber}_Marche (1500 pas = 20 min)`,
  ];
}

function buildCsvTemplateRows(weekCount) {
  // row1/row2/row3 reproduisent les 3 lignes d'en-tete du fichier cible.
  const row1 = [""];
  const row2 = [""];
  const row3 = ["ID participant"];

  for (let week = 1; week <= weekCount; week += 1) {
    if (week > 1) {
      row1.push("");
      row2.push("");
      row3.push("");
    }

    row1.push(`semaine ${String(week).padStart(2, "0")}`);
    row1.push(...Array(42).fill(""));

    row2.push("exercice");
    row2.push(...Array(7).fill(""));
    row2.push("difficulte");
    row2.push(...Array(8).fill(""));
    row2.push("douleur");
    row2.push(...Array(8).fill(""));
    row2.push("motivation");
    row2.push(...Array(8).fill(""));
    row2.push("marche");
    row2.push(...Array(7).fill(""));

    row3.push(...weekMetricHeaders(week));
  }

  return { row1, row2, row3 };
}

function normalizeSessionRow(session) {
  // Pre-calcule des champs numeriques pour simplifier les agregations hebdo.
  return {
    ...session,
    __date: dayjs(session?.date),
    __exercise: toCsvNumber(session?.accomplishedExercice),
    __difficulty: toCsvNumber(session?.difficultyLevel),
    __pain: toCsvNumber(session?.painLevel),
    __walking: toCsvNumber(session?.walkingTime),
  };
}

function firstSessionPerDay(weekSessions, weekStartDate) {
  // Si plusieurs sessions existent le meme jour, garder la plus tot.
  const slots = Array(7).fill(null);
  weekSessions.forEach((session) => {
    const dayIndex = session.__date.diff(weekStartDate, "day");
    if (dayIndex < 0 || dayIndex > 6) return;
    if (!slots[dayIndex]) {
      slots[dayIndex] = session;
      return;
    }
    if (session.__date.isBefore(slots[dayIndex].__date)) {
      slots[dayIndex] = session;
    }
  });
  return slots;
}

function summarizeWeek(weekSessions, weekStartDate) {
  // Construit toutes les cellules de sortie d'un bloc semaine (43 colonnes).
  const daySlots = firstSessionPerDay(weekSessions, weekStartDate);

  const sumMetric = (values) => {
    const numeric = values.filter((v) => Number.isFinite(v));
    if (numeric.length === 0) return CSV_MISSING_VALUE;
    return formatCsvNumber(numeric.reduce((acc, cur) => acc + cur, 0));
  };

  const avgMetric = (values) => {
    const numeric = values.filter((v) => Number.isFinite(v));
    if (numeric.length === 0) return CSV_MISSING_VALUE;
    const avg = numeric.reduce((acc, cur) => acc + cur, 0) / numeric.length;
    return formatCsvNumber(avg);
  };

  const exercisesRaw = daySlots.map((s) => (s ? s.__exercise : null));
  const difficultyRaw = daySlots.map((s) => (s ? s.__difficulty : null));
  const painRaw = daySlots.map((s) => (s ? s.__pain : null));
  const walkingRaw = daySlots.map((s) => (s ? s.__walking : null));
  const motivationRaw = Array(7).fill(null);

  const toCells = (values) =>
    values.map((v) => (Number.isFinite(v) ? formatCsvNumber(v) : CSV_MISSING_VALUE));

  const exercises = toCells(exercisesRaw);
  const difficulty = toCells(difficultyRaw);
  const pain = toCells(painRaw);
  const motivation = toCells(motivationRaw);
  const walking = toCells(walkingRaw);

  const weekCells = [];
  weekCells.push(...exercises, sumMetric(exercisesRaw));
  weekCells.push(...difficulty, sumMetric(difficultyRaw), avgMetric(difficultyRaw));
  weekCells.push(...pain, sumMetric(painRaw), avgMetric(painRaw));
  weekCells.push(...motivation, sumMetric(motivationRaw), avgMetric(motivationRaw));
  weekCells.push(...walking, sumMetric(walkingRaw));
  return weekCells;
}

function buildWeeklyCsvRow({ patientId, sessions, weekCount }) {
  // Groupe les sessions par semaine calendrier puis serialise selon le modele.
  const normalized = (Array.isArray(sessions) ? sessions : [])
    .map(normalizeSessionRow)
    .filter((s) => s.__date.isValid())
    .sort((a, b) => a.__date.valueOf() - b.__date.valueOf());

  const byWeek = new Map();
  normalized.forEach((session) => {
    const weekStart = session.__date.startOf("week").format("YYYY-MM-DD");
    if (!byWeek.has(weekStart)) byWeek.set(weekStart, []);
    byWeek.get(weekStart).push(session);
  });

  const orderedWeeks = Array.from(byWeek.keys()).sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());
  const selectedWeeks = orderedWeeks.slice(0, weekCount);

  const row = [patientId ?? CSV_MISSING_VALUE];
  for (let weekIndex = 0; weekIndex < weekCount; weekIndex += 1) {
    if (weekIndex > 0) row.push("");
    const weekStartKey = selectedWeeks[weekIndex];
    if (!weekStartKey) {
      row.push(...Array(43).fill(CSV_MISSING_VALUE));
      continue;
    }
    const weekStartDate = dayjs(weekStartKey);
    row.push(...summarizeWeek(byWeek.get(weekStartKey) || [], weekStartDate));
  }

  return row;
}

function escapeCsvCell(value) {
  if (value == null) return "";
  const text = String(value);
  if (text.includes(",") || text.includes("\"") || text.includes("\n")) {
    return `"${text.replace(/"/g, "\"\"")}"`;
  }
  return text;
}

function rowsToCsv(rows) {
  // Aligne la largeur des lignes pour un affichage stable dans Excel.
  const maxLen = rows.reduce((max, row) => Math.max(max, row.length), 0);
  return rows
    .map((row) => {
      const padded = row.concat(Array(Math.max(0, maxLen - row.length)).fill(""));
      return padded.map(escapeCsvCell).join(",");
    })
    .join("\n");
}

const EVAL_EXPORT_HEADERS = [
  "IDParticipant",
  "Age",
  "Sexe (F=0;H=1)",
  "T0_Poids (kg)",
  "T0_Taille (m)",
  "T0_Eq_PJ (s)",
  "T0_Eq_PJ_SPPB_SCORE",
  "T0_Eq_ST (s)",
  "T0_Eq_ST_SPPB_SCORE",
  "T0_Eq_T (s)",
  "T0_Eq_T_SPPB_SCORE",
  "T0_SPPB_Eq_SCORE",
  "T0_Eq_unip (s)",
  "T0_Eq_unip_Cote",
  "T0_STS30s_modalite (avec aide bras=0/sans aide bras=1)",
  "T0_STS 30sec (reps)",
  "T0_STS30sec_Clini",
  "T0_STS 5x (sec)",
  "T0_SPPB_5rep_SCORE",
  "T0_4m_AideTechnique",
  "T0_4m_Tps (sec)",
  "T0_4m_vitesse de marche (m/s)",
  "T0_SPPB_4m_SCORE",
  "T0_SPPB_TOTAL_SCORE",
  "T0_SPPBtot_Clini",
  "",
  "T1_Eq_PJ_Tps (s)",
  "T1_Eq_PJ_SPPB_SCORE",
  "T1_Eq_ST_Tps (s)",
  "T1_Eq_ST_SPPB_SCORE",
  "T1_Eq_T__Tps (s)",
  "T1_Eq_T_SPPB_SCORE",
  "T1_SPPB_Eq_SCORE",
  "T1_Eq_unip__Tps (s)",
  "T1_EQ_UNIP_COTATION",
  "T1_STS30s_modalite (avec aide bras=0/sans aide bras=1)",
  "T1_STS 30sec",
  "T1_STS30sec_Clini",
  "DeltaSTS 30sec (%)",
  "DeltaSTS 30sec (rep)",
  "T1_STS 5x (sec)",
  "DeltaSTS 5x (%)",
  "DeltaSTS 5x (sec)",
  "T1_SPPB_5rep_SCORE",
  "T1_4m_AideTechnique",
  "T1_TpsMarche4m",
  "Deltap_T0_T1_4Mn_Vitesse (%)",
  "Deltaa_T0T1_4Mn_Vitesse (m/s)",
  "T1_SPPB_4m_SCORE",
  "T1_SPPB_TOTAL_SCORE",
  "T1_SPPBtot_Clini",
  "Delta_T0T1_SPPBtot (pts)",
  "Delta_T0T1_SPPBtotSCORE_Clini",
];

function normalizePacePayload(evalPayload) {
  if (!evalPayload) return null;
  return evalPayload?.pace ?? evalPayload?.Evaluation_PACE ?? evalPayload;
}

function toNullableNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toSexCode(gender) {
  const value = String(gender || "").trim().toLowerCase();
  if (["female", "femme", "f", "feminin", "feminin(e)", "feminine"].includes(value)) return 0;
  if (["male", "homme", "h", "m", "masculin"].includes(value)) return 1;
  return "";
}

function toKg(weight, weightUnit) {
  const parsed = toNullableNumber(weight);
  if (parsed == null) return "";
  const unit = String(weightUnit || "").trim().toLowerCase();
  if (unit === "lbs" || unit === "lb") return parsed * 0.45359237;
  return parsed;
}

function toMeters(height) {
  const parsed = toNullableNumber(height);
  if (parsed == null) return "";
  if (parsed > 3) return parsed / 100;
  return parsed;
}

function toSupportCode(chairTestSupport) {
  if (chairTestSupport === true) return 0;
  if (chairTestSupport === false) return 1;
  const value = String(chairTestSupport ?? "").trim().toLowerCase();
  if (!value) return "";
  if (["with", "avec", "avec aide", "1", "true"].includes(value)) return 0;
  if (["without", "sans", "sans aide", "0", "false"].includes(value)) return 1;
  return "";
}
function toWalkingAidCode(walkingAid) {
  const value = String(walkingAid || "").trim().toLowerCase();
  if (!value || ["none", "aucune", "rien"].includes(value)) return 0;
  if (value.includes("marchette")) return 1;
  if (value.includes("canne")) return 2;
  if (value.includes("ambulateur")) return 3;
  if (value.includes("hemi") || value.includes("lat")) return 4;
  return "";
}

function safeDiff(a, b) {
  const av = toNullableNumber(a);
  const bv = toNullableNumber(b);
  if (av == null || bv == null) return "";
  return bv - av;
}

function safePercentDiff(a, b) {
  const av = toNullableNumber(a);
  const bv = toNullableNumber(b);
  if (av == null || bv == null || av === 0) return "";
  return ((bv - av) / av) * 100;
}

function safeWalkTimeFromSpeed(speed) {
  const v = toNullableNumber(speed);
  if (v == null || v <= 0) return "";
  return 4 / v;
}

function computeSppbBalanceScores(paceEval) {
  const feet = toNullableNumber(paceEval?.balanceFeetTogether);
  const semi = toNullableNumber(paceEval?.balanceSemiTandem);
  const tandem = toNullableNumber(paceEval?.balanceTandem);
  const hasAnyValue = [feet, semi, tandem].some((v) => v != null);
  if (!hasAnyValue) {
    return { pj: "", st: "", t: "", total: "" };
  }

  const pj = feet != null && feet >= 10 ? 1 : 0;
  const st = semi != null && semi >= 10 ? 1 : 0;
  let t = 0;
  if (tandem != null) {
    if (tandem >= 10) t = 2;
    else if (tandem >= 3) t = 1;
  }

  return { pj, st, t, total: pj + st + t };
}

function estimateSts5Time(chairCount, chairTestTime) {
  const directTime = toNullableNumber(chairTestTime);
  if (directTime != null && directTime > 0) return directTime;

  const count = toNullableNumber(chairCount);
  if (count == null || count <= 0) return "";
  return (5 * 30) / count;
}

function computeSppb5RepScore(sts5Time, supportCode) {
  const t = toNullableNumber(sts5Time);
  if (t == null) return "";
  if (supportCode === 0) return 0;
  if (t <= 0 || t > 60) return 0;
  if (t < 11.2) return 4;
  if (t < 13.7) return 3;
  if (t < 16.7) return 2;
  return 1;
}

function computeSppb4mScore(walkTimeSec) {
  const t = toNullableNumber(walkTimeSec);
  if (t == null) return "";
  if (t <= 0) return 0;
  if (t < 4.82) return 4;
  if (t < 6.21) return 3;
  if (t < 8.7) return 2;
  return 1;
}

function computeSppbTotalScore(balanceScore, fiveRepScore, walk4mScore) {
  const b = toNullableNumber(balanceScore);
  const f = toNullableNumber(fiveRepScore);
  const w = toNullableNumber(walk4mScore);
  if (b == null || f == null || w == null) return "";
  return b + f + w;
}

function buildDerivedPacePhase(paceEval) {
  const chairCount = toNullableNumber(paceEval?.chairTestCount);
  const chairTime = toNullableNumber(paceEval?.chairTestTime);
  const walkSpeed = toNullableNumber(paceEval?.vitesseDeMarche);
  const supportCode = toSupportCode(paceEval?.chairTestSupport);
  const walkTime4m = safeWalkTimeFromSpeed(walkSpeed);
  const balanceScores = computeSppbBalanceScores(paceEval);
  const sts5TimeEstimated = estimateSts5Time(chairCount, chairTime);
  const sppb5RepScore = computeSppb5RepScore(sts5TimeEstimated, supportCode);
  const sppb4mScore = computeSppb4mScore(walkTime4m);
  const sppbTotal = computeSppbTotalScore(
    balanceScores.total,
    sppb5RepScore,
    sppb4mScore
  );

  return {
    chairCount,
    chairTime,
    walkSpeed,
    supportCode,
    walkTime4m,
    balanceScores,
    sts5TimeEstimated,
    sppb5RepScore,
    sppb4mScore,
    sppbTotal,
  };
}

function computeSppbClinicalCode(totalScore) {
  const total = toNullableNumber(totalScore);
  if (total == null) return "";
  if (total <= 6) return "Faible performance";
  if (total <= 9) return "Performance moderee";
  return "Bonne performance";
}

function computeDeltaClinicalCode(deltaValue) {
  const delta = toNullableNumber(deltaValue);
  if (delta == null) return "";
  if (delta >= 1) return "Amelioration";
  if (delta <= -1) return "Deterioration";
  return "Stable";
}

function computeSts30ClinicalCode(age, sex, count) {
  const risk = sts30Risk(age, sex, count);
  if (risk == null) return "";
  return risk ? "A risque" : "Non a risque";
}

function formatExportCell(value) {
  if (value === "" || value == null) return "";
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.round(value * 1000) / 1000;
  }
  return value;
}

function finalizeEvaluationRow(row) {
  return row.map((value, idx) => {
    const header = EVAL_EXPORT_HEADERS[idx];
    if (!header) return "";
    const formatted = formatExportCell(value);
    if (formatted === "" || formatted == null) return CSV_MISSING_VALUE;
    return formatted;
  });
}

function buildEvaluationExportRow({ patient, t0, t1, fallbackAge, fallbackSex }) {
  const age = patientAgeYears(patient) ?? toNullableNumber(fallbackAge);
  const sexCode = toSexCode(patient?.gender ?? fallbackSex);
  const weightKg = toKg(patient?.weight, patient?.weightUnit);
  const heightM = toMeters(patient?.height);

  const t0Derived = buildDerivedPacePhase(t0);
  const t1Derived = buildDerivedPacePhase(t1);
  const t0Chair = t0Derived.chairCount;
  const t1Chair = t1Derived.chairCount;
  const t0Speed = t0Derived.walkSpeed;
  const t1Speed = t1Derived.walkSpeed;
  const t0SupportCode = t0Derived.supportCode;
  const t1SupportCode = t1Derived.supportCode;
  const t0BalanceScores = t0Derived.balanceScores;
  const t1BalanceScores = t1Derived.balanceScores;
  const t0Sts30Clinical = computeSts30ClinicalCode(age, patient?.gender ?? fallbackSex, t0Chair);
  const t1Sts30Clinical = computeSts30ClinicalCode(age, patient?.gender ?? fallbackSex, t1Chair);

  const t0WalkTime = t0Derived.walkTime4m;
  const t1WalkTime = t1Derived.walkTime4m;
  const t0Sts5Estimated = t0Derived.sts5TimeEstimated;
  const t1Sts5Estimated = t1Derived.sts5TimeEstimated;
  const t0Sppb5RepScore = t0Derived.sppb5RepScore;
  const t1Sppb5RepScore = t1Derived.sppb5RepScore;
  const t0Sppb4mScore = t0Derived.sppb4mScore;
  const t1Sppb4mScore = t1Derived.sppb4mScore;
  const t0SppbTotal = t0Derived.sppbTotal;
  const t1SppbTotal = t1Derived.sppbTotal;
  const deltaSppbTotal = safeDiff(t0SppbTotal, t1SppbTotal);

  return finalizeEvaluationRow([
    patient?.id ?? "",
    age ?? "",
    sexCode,
    weightKg,
    heightM,
    t0?.balanceFeetTogether ?? "",
    t0BalanceScores.pj,
    t0?.balanceSemiTandem ?? "",
    t0BalanceScores.st,
    t0?.balanceTandem ?? "",
    t0BalanceScores.t,
    t0BalanceScores.total,
    t0?.balanceOneFooted ?? "",
    t0?.supportFootUnipodal ?? "",
    t0SupportCode,
    t0Chair ?? "",
    t0Sts30Clinical,
    t0Sts5Estimated,
    t0Sppb5RepScore,
    toWalkingAidCode(t0?.walkingAid),
    t0WalkTime,
    t0Speed ?? "",
    t0Sppb4mScore,
    t0SppbTotal,
    computeSppbClinicalCode(t0SppbTotal),
    "",
    t1?.balanceFeetTogether ?? "",
    t1BalanceScores.pj,
    t1?.balanceSemiTandem ?? "",
    t1BalanceScores.st,
    t1?.balanceTandem ?? "",
    t1BalanceScores.t,
    t1BalanceScores.total,
    t1?.balanceOneFooted ?? "",
    t1?.supportFootUnipodal ?? "",
    t1SupportCode,
    t1Chair ?? "",
    t1Sts30Clinical,
    safePercentDiff(t0Chair, t1Chair),
    safeDiff(t0Chair, t1Chair),
    t1Sts5Estimated,
    safePercentDiff(t0Sts5Estimated, t1Sts5Estimated),
    safeDiff(t0Sts5Estimated, t1Sts5Estimated),
    t1Sppb5RepScore,
    toWalkingAidCode(t1?.walkingAid),
    t1WalkTime,
    safePercentDiff(t0Speed, t1Speed),
    safeDiff(t0Speed, t1Speed),
    t1Sppb4mScore,
    t1SppbTotal,
    computeSppbClinicalCode(t1SppbTotal),
    deltaSppbTotal,
    computeDeltaClinicalCode(deltaSppbTotal),
  ]);
}
function toFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function average(values) {
  if (!Array.isArray(values) || values.length === 0) return null;
  const sum = values.reduce((acc, cur) => acc + cur, 0);
  return sum / values.length;
}

function normalizeSex(value) {
  if (value == null) return null;
  const v = String(value).trim().toLowerCase();
  if (!v) return null;
  if (["f", "female", "femme", "feminin", "féminin", "féminin"].includes(v)) return "F";
  if (["h", "m", "male", "homme", "masculin"].includes(v)) return "H";
  return null;
}

function patientAgeYears(patient) {
  const rawDate = patient?.birthday ?? patient?.dateOfBirth ?? patient?.dob ?? patient?.birthDate;
  if (!rawDate) return null;
  const parsed = dayjs(rawDate);
  if (!parsed.isValid()) return null;
  return dayjs().diff(parsed, "year");
}

function sts30Risk(age, sex, count) {
  const a = Number(age);
  const c = Number(count);
  if (!Number.isFinite(a) || !Number.isFinite(c)) return null;
  if (a < 60 || a > 94) return null;
  const s = normalizeSex(sex);
  if (!s) return null;

  const femaleRules = [
    { min: 60, max: 64, th: 12 },
    { min: 65, max: 69, th: 11 },
    { min: 70, max: 74, th: 10 },
    { min: 75, max: 79, th: 10 },
    { min: 80, max: 84, th: 9 },
    { min: 85, max: 89, th: 8 },
    { min: 90, max: 94, th: 4 },
  ];
  const maleRules = [
    { min: 60, max: 64, th: 14 },
    { min: 65, max: 69, th: 12 },
    { min: 70, max: 74, th: 12 },
    { min: 75, max: 79, th: 12 },
    { min: 80, max: 84, th: 10 },
    { min: 85, max: 89, th: 8 },
    { min: 90, max: 94, th: 7 },
  ];

  const rules = s === "F" ? femaleRules : maleRules;
  const rule = rules.find((r) => a >= r.min && a <= r.max);
  if (!rule) return null;
  return c < rule.th;
}

function buildMetricStats(rows, fieldOrGetter) {
  const getValue =
    typeof fieldOrGetter === "function"
      ? fieldOrGetter
      : (phase) => phase?.[fieldOrGetter];
  const t0Values = [];
  const t1Values = [];

  rows.forEach((row) => {
    const t0 = toFiniteNumber(getValue(row?.t0, row, "t0"));
    const t1 = toFiniteNumber(getValue(row?.t1, row, "t1"));
    if (t0 != null) t0Values.push(t0);
    if (t1 != null) t1Values.push(t1);
  });

  const t0Mean = average(t0Values);
  const t1Mean = average(t1Values);

  return {
    nT0: t0Values.length,
    nT1: t1Values.length,
    t0Mean,
    t1Mean,
    deltaMean: t0Mean != null && t1Mean != null ? t1Mean - t0Mean : null,
    t0Values,
    t1Values,
  };
}

function buildSts30RiskSummary(rows, phase) {
  const key = phase === "T0" ? "t0" : "t1";
  let known = 0;
  let atRisk = 0;
  let notAtRisk = 0;

  rows.forEach((row) => {
    const count = toFiniteNumber(row?.[key]?.chairTestCount);
    if (count == null) return;
    const risk = sts30Risk(row?.age, row?.sex, count);
    if (risk == null) return;
    known += 1;
    if (risk) atRisk += 1;
    else notAtRisk += 1;
  });

  return { known, atRisk, notAtRisk };
}

function buildGlobalPaceStats(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  const enrichedRows = rows.map((row) => ({
    ...row,
    __t0Derived: buildDerivedPacePhase(row?.t0),
    __t1Derived: buildDerivedPacePhase(row?.t1),
  }));

  const count = rows.filter((row) => Boolean(row?.t0)).length;
  const countT1 = rows.filter((row) => Boolean(row?.t1)).length;

  return {
    count,
    countT1,
    tests: {
      sppb: buildMetricStats(
        enrichedRows,
        (_phase, row, phase) =>
          phase === "t0" ? row?.__t0Derived?.sppbTotal : row?.__t1Derived?.sppbTotal
      ),
      chair: {
        ...buildMetricStats(enrichedRows, "chairTestCount"),
        riskT0: buildSts30RiskSummary(enrichedRows, "T0"),
        riskT1: buildSts30RiskSummary(enrichedRows, "T1"),
      },
      sts5x: buildMetricStats(
        enrichedRows,
        (_phase, row, phase) =>
          phase === "t0"
            ? row?.__t0Derived?.sts5TimeEstimated
            : row?.__t1Derived?.sts5TimeEstimated
      ),
      equil: buildMetricStats(enrichedRows, "scoreB"),
      vitesse: buildMetricStats(enrichedRows, "vitesseDeMarche"),
      frt: buildMetricStats(enrichedRows, "frtDistance"),
    },
  };
}

function PaceT0T1Status({ status, message }) {
  if (status === "loading") return <Spin />;
  if (status === "error") {
    return <Alert type="error" showIcon message={message} />;
  }
  return null;
}

PaceT0T1Status.propTypes = {
  status: PropTypes.oneOf(["idle", "loading", "error"]).isRequired,
  message: PropTypes.string.isRequired,
};

export const __patientStatsTestables = {
  toCsvNumber,
  formatCsvNumber,
  weekMetricHeaders,
  buildCsvTemplateRows,
  normalizeSessionRow,
  firstSessionPerDay,
  summarizeWeek,
  buildWeeklyCsvRow,
  escapeCsvCell,
  rowsToCsv,
  normalizePacePayload,
  toNullableNumber,
  toSexCode,
  toKg,
  toMeters,
  toSupportCode,
  toWalkingAidCode,
  safeDiff,
  safePercentDiff,
  safeWalkTimeFromSpeed,
  computeSppbBalanceScores,
  estimateSts5Time,
  computeSppb5RepScore,
  computeSppb4mScore,
  computeSppbTotalScore,
  buildDerivedPacePhase,
  computeSppbClinicalCode,
  computeDeltaClinicalCode,
  computeSts30ClinicalCode,
  formatExportCell,
  finalizeEvaluationRow,
  buildEvaluationExportRow,
  toFiniteNumber,
  average,
  normalizeSex,
  patientAgeYears,
  sts30Risk,
  buildMetricStats,
  buildSts30RiskSummary,
  buildGlobalPaceStats,
  EVAL_EXPORT_HEADERS,
};

export default function PatientStats({ patient, patients, onClose = null, programType, setProgramType, onExportCsvReady = null }) {
  const { t } = useTranslation("Patients");
  const { token } = useToken();
// --- Etat ---
// Pour le type de programme selectionne
//const [programType, setProgramType] = useState("PACE");

const [selectedPatientId, setSelectedPatientId] = useState(
  patient?.id ?? patients?.[0]?.id ?? null
);
const [selectedClinicId, setSelectedClinicId] = useState("all");
const normalizedPatients = useMemo(
  () => (Array.isArray(patients) ? patients : []),
  [patients]
);
const clinicOptions = useMemo(
  () => buildClinicOptions(normalizedPatients),
  [normalizedPatients]
);
const clinicScopedPatients = useMemo(
  () => filterPatientsByClinic(normalizedPatients, selectedClinicId),
  [normalizedPatients, selectedClinicId]
);
// Filtre utilise uniquement en mode "Tous" (vue statistiques globales).
const [allPatientsEvalFilter, setAllPatientsEvalFilter] = useState("all");
// "all" dans le select = page tous les patients, pas un patient unique.
const isAllSelected = String(selectedPatientId) === "all";
const selectedPatient = useMemo(
  () =>
    resolveSelectedPatient({
      clinicScopedPatients,
      selectedPatientId,
      patient,
      selectedClinicId,
    }),
  [clinicScopedPatients, selectedPatientId, patient, selectedClinicId]
);

useEffect(() => {
  const replacementPatientId = getReplacementSelectedPatientId(
    clinicScopedPatients,
    selectedPatientId
  );
  if (replacementPatientId !== null) {
    setSelectedPatientId(replacementPatientId);
  }
}, [clinicScopedPatients, selectedPatientId]);

const selectedPatientRecordId = selectedPatient?.id ?? null;



  // --- URLs ---
const patientSessionsUrl = selectedPatientRecordId
  ? `${Constants.SERVER_URL}/patient/${selectedPatientRecordId}/sessions`
  : null;
const paceT0T1Url = selectedPatientId
  ? `${Constants.SERVER_URL}/stats/patients/${selectedPatientId}/t0t1?type=PACE`
  : null;

//const programsUrl = `${Constants.SERVER_URL}/programs/${selectedPatientRecordId}`;
const programsUrl = selectedPatientRecordId
  ? `${Constants.SERVER_URL}/programs/programs/${selectedPatientRecordId}`
  : null;


  // --- Chargement des donnees ---
  const fetchConfig = { headers: { Authorization: "Bearer " + token } };
  const allPatientIds = useMemo(
    () => (Array.isArray(clinicScopedPatients) ? clinicScopedPatients : [])
      .map((p) => Number(p?.id))
      .filter((id) => Number.isFinite(id)),
    [clinicScopedPatients]
  );

  const { data: sessionsData } = useQuery(
    ["SessionRecord", selectedPatientRecordId],
    () => axios.get(patientSessionsUrl, fetchConfig).then((res) => res.data),
    { enabled: !!token && !!selectedPatientRecordId && !isAllSelected }
  );
  const { data: allPrograms } = useQuery(
    ["AllPrograms", selectedPatientRecordId],
    () => axios.get(programsUrl, fetchConfig).then((res) => res.data),
    { enabled: !!token && !!selectedPatientRecordId && !isAllSelected }
  );
const {
  data: paceT0T1,
  isLoading: isLoadingPaceT0T1,
  isError: isErrorPaceT0T1,
} = useQuery(
  ["PACE_T0T1", selectedPatientId, token],
  () => axios.get(paceT0T1Url, fetchConfig).then((res) => res.data),
  {
    enabled: !!token && !!selectedPatientId && programType === "PACE" && !isAllSelected,
    staleTime: 60_000,
  }
);

const {
  data: allPatientsT0T1Rows = [],
  isLoading: isLoadingAllPatientsEvals,
} = useQuery(
  ["AllPatientsPACE_T0T1", token, programType, allPatientIds.join(",")],
  async () => {
    const patientMap = new Map(
      (Array.isArray(clinicScopedPatients) ? clinicScopedPatients : []).map((p) => [Number(p.id), p])
    );
    // Charge un payload t0/t1 par patient puis normalise en tableau plat.
    const rows = await Promise.all(
      allPatientIds.map(async (id) => {
        try {
          const res = await axios.get(
            `${Constants.SERVER_URL}/stats/patients/${id}/t0t1?type=PACE`,
            fetchConfig
          );
          const payload = res?.data || null;
          const person = patientMap.get(id);
          return {
            patientId: id,
            age: patientAgeYears(person),
            sex: person?.gender,
            // Compatibilite: certaines versions du backend renvoient "pace", d'autres "Evaluation_PACE"
            t0: payload?.t0?.pace ?? payload?.t0?.Evaluation_PACE ?? null,
            t1: payload?.t1?.pace ?? payload?.t1?.Evaluation_PACE ?? null,
          };
        } catch (err) {
          const person = patientMap.get(id);
          return {
            patientId: id,
            age: patientAgeYears(person),
            sex: person?.gender,
            t0: null,
            t1: null,
          };
        }
      })
    );
    return rows;
  },
  {
    enabled: !!token && isAllSelected && programType === "PACE" && allPatientIds.length > 0,
    staleTime: 60_000,
  }
);

const {
  data: allPatientsSessions = [],
  isLoading: isLoadingAllPatientsSessions,
} = useQuery(
  ["AllPatientsSessions", token, programType, allPatientIds.join(",")],
  async () => {
    // Recupere les sessions de chaque patient puis fusionne avec un tag patient.
    const allResults = await Promise.all(
      allPatientIds.map(async (id) => {
        try {
          const res = await axios.get(`${Constants.SERVER_URL}/patient/${id}/sessions`, fetchConfig);
          const rows = Array.isArray(res.data) ? res.data : [];
          return rows.map((row) => ({ ...row, __patientId: id }));
        } catch {
          return [];
        }
      })
    );
    return allResults.flat();
  },
  {
    enabled: !!token && isAllSelected && programType === "PACE" && allPatientIds.length > 0,
    staleTime: 60_000,
  }
);

const filteredAllPatientsT0T1Rows = useMemo(() => {
  const rows = Array.isArray(allPatientsT0T1Rows) ? allPatientsT0T1Rows : [];
  const hasT1 = (row) => Boolean(row?.t1);

  if (allPatientsEvalFilter === "t1_done") {
    return rows.filter((row) => hasT1(row));
  }

  // "Tous": tous les patients (avec ou sans T1).
  return rows;
}, [allPatientsT0T1Rows, allPatientsEvalFilter]);

const filteredAllPatientIds = useMemo(
  () =>
    new Set(
      filteredAllPatientsT0T1Rows
        .map((row) => Number(row?.patientId))
        .filter((id) => Number.isFinite(id))
    ),
  [filteredAllPatientsT0T1Rows]
);

const filteredAllPatientsList = useMemo(
  () =>
    (Array.isArray(clinicScopedPatients) ? clinicScopedPatients : []).filter((p) =>
      filteredAllPatientIds.has(Number(p?.id))
    ),
  [clinicScopedPatients, filteredAllPatientIds]
);

const filteredAllPatientsSessions = useMemo(
  () =>
    (Array.isArray(allPatientsSessions) ? allPatientsSessions : []).filter((row) =>
      filteredAllPatientIds.has(Number(row?.__patientId))
    ),
  [allPatientsSessions, filteredAllPatientIds]
);

const filteredAllPatientsEvalsData = useMemo(
  () => (programType === "PACE" ? buildGlobalPaceStats(filteredAllPatientsT0T1Rows) : null),
  [programType, filteredAllPatientsT0T1Rows]
);


  // --- Etat ---
  // Etat programme


  // Etat des sessions
  const sessions = useMemo(() => sessionsData || [], [sessionsData]);
  const hasSessions = sessions.length > 0;

  // Plage de dates selectionnee pour les moyennes
  const [dateRange, setDateRange] = useState([null, null]);

  // Mode de navigation des periodes
  const [averageViewMode, setAverageViewMode] = useState('month'); // 'week', 'month', 'quarter', 'year'
  const [currentPeriodDate, setCurrentPeriodDate] = useState(dayjs());

  // --- Donnees derivees (memoisees) ---

  // Calcule la plage de dates selon l'intervalle choisi
  const getCurrentPeriodRange = useCallback(() => {
    // Utilise une reference stable de la date courante
    const date = currentPeriodDate;
    let start, end;

    switch (averageViewMode) {
      case 'week':
        start = dayjs(date).startOf('week');
        end = dayjs(date).endOf('week');
        break;
      case 'month':
        start = dayjs(date).startOf('month');
        end = dayjs(date).endOf('month');
        break;
      case 'quarter':
        start = dayjs(date).startOf('quarter');
        end = dayjs(date).endOf('quarter');
        break;
      case 'year':
        start = dayjs(date).startOf('year');
        end = dayjs(date).endOf('year');
        break;
      default:
        start = dayjs(date).startOf('month');
        end = dayjs(date).endOf('month');
    }

    // Retourne des objets date stables
    return [start, end];
  }, [currentPeriodDate, averageViewMode]);

  const memoizedDateRange = useMemo(() => {
    const [start, end] = getCurrentPeriodRange();
    return [start, end];
  }, [getCurrentPeriodRange]);

  // Filtre les sessions selon la plage de dates selectionnee
  const filteredSessions = useMemo(() => {
    const start = dateRange[0];
    const end = dateRange[1];

    let filtered = sessions;

    if (start && end) {
      const startDate = dayjs(start).startOf('day');
      const endDate = dayjs(end).endOf('day');

      filtered = sessions.filter(session => {
        if (!session.date) return false;
        const sessionDate = dayjs(session.date);
        return !sessionDate.isBefore(startDate) && !sessionDate.isAfter(endDate);
      });
    }

    return filtered;
  }, [sessions, dateRange]);

  // Calcule les moyennes sur les sessions filtrees
  const rangeAverages = useMemo(() => {
    if (filteredSessions.length === 0) return null;

    const averageData = filteredSessions.reduce(
      (acc, s) => {
        acc.difficultyLevel += Number(s.difficultyLevel) || 0;
        acc.painLevel += Number(s.painLevel) || 0;
        acc.accomplishedExercice += Number(s.accomplishedExercice) || 0;
        acc.walkingTime += Number(s.walkingTime) || 0;
        return acc;
      },
      { difficultyLevel: 0, painLevel: 0, walkingTime: 0, accomplishedExercice: 0 }
    );

    const totalFilteredSessions = filteredSessions.length;
    return {
      difficulty: (averageData.difficultyLevel / totalFilteredSessions).toFixed(1),
      pain: (averageData.painLevel / totalFilteredSessions).toFixed(1),
      walking: (averageData.walkingTime / totalFilteredSessions).toFixed(1),
      exercises: (averageData.accomplishedExercice / totalFilteredSessions).toFixed(1),
      count: totalFilteredSessions
    };
  }, [filteredSessions]);

  // Donnees du graphe de progression (LineChart) sur les sessions filtrees
  const rangeProgressionData = useMemo(() => {
    return filteredSessions.map((session, index) => ({
      date: dayjs(session.date).format('YYYY-MM-DD'),
      sessionIndex: sessions.findIndex(s => s.id === session.id) + 1,
      difficulty: Number(session.difficultyLevel),
      pain: Number(session.painLevel),
      walking: Number(session.walkingTime),
      exercises: Number(session.accomplishedExercice)
    })).sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
  }, [filteredSessions, sessions]);

  // --- Effets ---
  useEffect(() => {
    // Met a jour seulement si la plage de dates est vraiment differente
    const currentRangeStart = dateRange[0]?.format('YYYY-MM-DD');
    const currentRangeEnd = dateRange[1]?.format('YYYY-MM-DD');
    const newRangeStart = memoizedDateRange[0]?.format('YYYY-MM-DD');
    const newRangeEnd = memoizedDateRange[1]?.format('YYYY-MM-DD');

    if (currentRangeStart !== newRangeStart || currentRangeEnd !== newRangeEnd) {
      setDateRange(memoizedDateRange);
    }
  }, [memoizedDateRange, dateRange]);

  // --- Autres donnees derivees / variables ---
  const patientPrograms = useMemo(() => allPrograms || [], [allPrograms]);
  const programNameFromEvaluation = useMemo(() => {
    const t0ProgramName = paceT0T1?.t0?.Program?.name;
    const t1ProgramName = paceT0T1?.t1?.Program?.name;
    if (t0ProgramName) return t0ProgramName;
    if (t1ProgramName) return t1ProgramName;
    return patientPrograms?.[0]?.name || null;
  }, [paceT0T1, patientPrograms]);

  const plannedEvaluationDates = useMemo(() => {
    const planningT0 = paceT0T1?.t0?.programPlanning ?? null;
    const planningT1 = paceT0T1?.t1?.programPlanning ?? null;

    const t0Planned = planningT0?.programStartDate ?? null;

    const nextDates =
      (Array.isArray(planningT0?.nextEvaluationDates) && planningT0.nextEvaluationDates) ||
      (Array.isArray(planningT1?.nextEvaluationDates) && planningT1.nextEvaluationDates) ||
      [];
    const t1Planned = nextDates.length > 0 ? nextDates[0] : null;

    return { t0Planned, t1Planned };
  }, [paceT0T1]);


  // Calcule les moyennes globales (si aucune plage n'est selectionnee)
  const overallAverages = useMemo(() => {
    if (!hasSessions) return null;
    const allSessionsAverageData = sessions.reduce(
      (acc, s) => {
        acc.difficultyLevel += Number(s.difficultyLevel) || 0;
        acc.painLevel += Number(s.painLevel) || 0;
        acc.accomplishedExercice += Number(s.accomplishedExercice) || 0;
        acc.walkingTime += Number(s.walkingTime) || 0;
        return acc;
      },
      { difficultyLevel: 0, painLevel: 0, walkingTime: 0, accomplishedExercice: 0 }
    );
    const totalSessions = sessions.length;
    return {
      difficulty: (allSessionsAverageData.difficultyLevel / totalSessions).toFixed(1),
      pain: (allSessionsAverageData.painLevel / totalSessions).toFixed(1),
      walking: (allSessionsAverageData.walkingTime / totalSessions).toFixed(1),
      exercises: (allSessionsAverageData.accomplishedExercice / totalSessions).toFixed(1),
      count: totalSessions
    };
  }, [sessions, hasSessions]);

  const averagesToDisplay = dateRange[0] && dateRange[1] ? rangeAverages : overallAverages;

  // Memoise separement les donnees mappees des sessions
  const allSessionsProgressionData = useMemo(() =>
    sessions.map((session, index) => ({
      date: dayjs(session.date).format('YYYY-MM-DD'),
      sessionIndex: index + 1,
      difficulty: Number(session.difficultyLevel),
      pain: Number(session.painLevel),
      walking: Number(session.walkingTime),
      exercises: Number(session.accomplishedExercice)
    })).sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))
    , [sessions]);

  const progressionDataToDisplay = dateRange[0] && dateRange[1]
    ? rangeProgressionData
    : allSessionsProgressionData;

  // Construit le libelle de la periode courante
  const getPeriodLabel = useCallback(() => {
    const date = currentPeriodDate;

    switch (averageViewMode) {
      case 'week':
        return t("stats_period_week_of", {
          date: date.startOf("week").format("DD/MM/YYYY"),
          defaultValue: "Week of {{date}}",
        });
      case 'month':
        return date.format('MMMM YYYY');
      case 'quarter':
        return `${date.quarter()}e trimestre ${date.year()}`;
      case 'year':
        return date.format('YYYY');
      default:
        return date.format('MMMM YYYY');
    }
  }, [currentPeriodDate, averageViewMode, t]);

  // Verifie si la periode courante est dans le futur
  const isFuturePeriod = useCallback(() => {
    const now = dayjs();

    switch (averageViewMode) {
      case 'week':
        return currentPeriodDate.startOf('week').isAfter(now.startOf('week'));
      case 'month':
        return currentPeriodDate.startOf('month').isAfter(now.startOf('month'));
      case 'quarter':
        return currentPeriodDate.startOf('quarter').isAfter(now.startOf('quarter'));
      case 'year':
        return currentPeriodDate.startOf('year').isAfter(now.startOf('year'));
      default:
        return false;
    }
  }, [currentPeriodDate, averageViewMode]);

  // --- Handlers d'evenements ---
  const handleRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
      setCurrentPeriodDate(dates[0]);
    } else {
      setDateRange([null, null]);
    }
  };

  const handlePeriodChange = useCallback((direction) => {
    setCurrentPeriodDate(prevDate => {
      let newDate = dayjs(prevDate);

      if (direction === 'previous') {
        switch (averageViewMode) {
          case 'week':
            newDate = newDate.subtract(1, 'week');
            break;
          case 'month':
            newDate = newDate.subtract(1, 'month');
            break;
          case 'quarter':
            newDate = newDate.subtract(1, 'quarter');
            break;
          case 'year':
            newDate = newDate.subtract(1, 'year');
            break;
          default:
            break;
        }
      } else if (direction === 'next') {
        switch (averageViewMode) {
          case 'week':
            newDate = newDate.add(1, 'week');
            break;
          case 'month':
            newDate = newDate.add(1, 'month');
            break;
          case 'quarter':
            newDate = newDate.add(1, 'quarter');
            break;
          case 'year':
            newDate = newDate.add(1, 'year');
            break;
          default:
            break;
        }
      }

      return newDate;
    });
    // Ne pas mettre a jour dateRange ici: l'effect s'en charge apres changement de periode
  }, [averageViewMode]); // Depend uniquement de averageViewMode, pas des dates courantes

  // Export CSV aligne sur la structure "donnees programme-RxAPA.xlsx".
  const downloadCSV = useCallback(() => {
    const today = dayjs().format("YYYY-MM-DD");
    const isPace = String(programType || "").toUpperCase() === "PACE";
    const csvRows = buildPatientStatsCsvRows({
      isPace,
      isAllSelected,
      selectedPatient,
      filteredAllPatientsList,
      filteredAllPatientsT0T1Rows,
      paceT0T1,
      filteredAllPatientsSessions,
      sessions,
      weekCount: CSV_WEEK_COUNT,
      missingValue: CSV_MISSING_VALUE,
      buildEvaluationExportRow,
      normalizePacePayload,
      buildCsvTemplateRows,
      buildWeeklyCsvRow,
      evalExportHeaders: EVAL_EXPORT_HEADERS,
    });
    const csvContent = `\ufeff${rowsToCsv(csvRows)}`;
    const fileName = buildCsvFileName({
      isAllSelected,
      isPace,
      selectedPatient,
      today,
    });
    saveAs(new Blob([csvContent], { type: "text/csv;charset=utf-8;" }), fileName);
  }, [
    programType,
    isAllSelected,
    selectedPatient,
    paceT0T1,
    sessions,
    filteredAllPatientsList,
    filteredAllPatientsSessions,
    filteredAllPatientsT0T1Rows,
  ]);

  useEffect(() => {
    // Expose la fonction d'export courante au bouton d'en-tete (composant parent).
    if (typeof onExportCsvReady !== "function") return;
    onExportCsvReady(downloadCSV);
  }, [onExportCsvReady, downloadCSV]);

  useEffect(() => {
    if (typeof onExportCsvReady !== "function") return undefined;
    return () => onExportCsvReady(null);
  }, [onExportCsvReady]);

  const paceStatusState = getPaceStatusState(
    isLoadingPaceT0T1,
    isErrorPaceT0T1
  );

  // --- Rendu JSX ---
  if (isAllSelected) {
    return (
      <div className="patient-data-container">
        <div className="side-by-side-container">
          <div className="side-by-side-card" style={{ width: "100%" }}>
            <PatientFiltersCard
              patients={clinicScopedPatients}
              selectedPatientId={selectedPatientId}
              onPatientChange={setSelectedPatientId}
              selectedClinicId={selectedClinicId}
              onClinicChange={setSelectedClinicId}
              clinicOptions={clinicOptions}
              programType={programType}
              onProgramTypeChange={setProgramType}
              evalFilter={allPatientsEvalFilter}
              onEvalFilterChange={setAllPatientsEvalFilter}
              showEvaluationFilter
              t={t}
            />
          </div>
        </div>
        <AllPatientsStats
          patients={filteredAllPatientsList}
          programType={programType}
          setProgramType={setProgramType}
          evalsData={filteredAllPatientsEvalsData}
          isLoadingEvals={isLoadingAllPatientsEvals}
          sessions={filteredAllPatientsSessions}
          isLoadingSessions={isLoadingAllPatientsSessions}
          showFilters={false}
          embedded
        />
      </div>
    );
  }

  if (!selectedPatient) {
    return (
      <div className="patient-data-container">
        <div className="side-by-side-container">
          <div className="side-by-side-card" style={{ width: "100%" }}>
            <PatientFiltersCard
              patients={clinicScopedPatients}
              selectedPatientId={selectedPatientId}
              onPatientChange={setSelectedPatientId}
              selectedClinicId={selectedClinicId}
              onClinicChange={setSelectedClinicId}
              clinicOptions={clinicOptions}
              programType={programType}
              onProgramTypeChange={setProgramType}
              evalFilter={allPatientsEvalFilter}
              onEvalFilterChange={setAllPatientsEvalFilter}
              showEvaluationFilter={false}
              t={t}
            />
          </div>
        </div>
        <Alert
          type="info"
          showIcon
          message={t(
            "stats_no_patient_for_selected_clinic",
            "No patient found for the selected clinic."
          )}
        />
      </div>
    );
  }

  return (
    
    <div className="patient-data-container">
  <div className="side-by-side-container">
    <div className="side-by-side-card" style={{ width: "100%" }}>
      <PatientFiltersCard
        patients={clinicScopedPatients}
        selectedPatientId={selectedPatientId}
        onPatientChange={setSelectedPatientId}
        selectedClinicId={selectedClinicId}
        onClinicChange={setSelectedClinicId}
        clinicOptions={clinicOptions}
        programType={programType}
        onProgramTypeChange={setProgramType}
        evalFilter={allPatientsEvalFilter}
        onEvalFilterChange={setAllPatientsEvalFilter}
        showEvaluationFilter={false}
        t={t}
      />
    </div>
  </div>
      

      <div className="side-by-side-container">
        <div className="side-by-side-card" style={{ width: "100%" }}>

         

          <h2 className="section-header">{t("stats_section_information", "Information")}</h2>
           {/* Insertion de Infocard*/}
          <InfoCard
  patient={selectedPatient}
  t={t}
  programName={programNameFromEvaluation}
evaluationT0={paceT0T1?.t0?.createdAt ?? null}
evaluationT1={paceT0T1?.t1?.createdAt ?? null}
plannedEvaluationT0={plannedEvaluationDates.t0Planned}
plannedEvaluationT1={plannedEvaluationDates.t1Planned}
/>

        {programType === "PACE" && paceStatusState !== "idle" && (
  <div style={{ marginTop: 12 }}>
    <PaceT0T1Status
      status={paceStatusState}
      message={t("stats_error_loading_pace_t0_t1", "Error: /patient/:id/pace/t0t1")}
    />
  </div>
)}

        </div>
      </div>

      <div className="side-by-side-container">
        <div className="side-by-side-card">
         

            <SuiviEvaluationsCard
            patient={selectedPatient}
            programType={programType}
            paceT0T1={paceT0T1}
            isLoading={isLoadingPaceT0T1}
            isError={isErrorPaceT0T1}
          />

         
        </div>

        {/* --- Section des moyennes --- */}
        <div className="side-by-side-card">
          <h2 className="section-header">{t("stats_section_session_average", "Session averages")}</h2>
          <Card>

            <div className="average-vertical-layout">
              <div className="average-controls-container">
                <div className="average-view-selector">
                  <Radio.Group value={averageViewMode} onChange={(e) => setAverageViewMode(e.target.value)}>
                    <Radio.Button value="week">{t("stats_period_week", "Week")}</Radio.Button>
                    <Radio.Button value="month">{t("stats_period_month", "Month")}</Radio.Button>
                    <Radio.Button value="quarter">{t("stats_period_quarter", "Quarter")}</Radio.Button>
                    <Radio.Button value="year">{t("stats_period_year", "Year")}</Radio.Button>
                  </Radio.Group>
                </div>

                <div className="average-date-selector">
                  <label>{t("stats_filter_average_by_date", "Filter averages by date:")}</label>
                  <div>
                    <RangePicker
                      onChange={handleRangeChange}
                      locale={locale}
                      disabled={!hasSessions}
                      allowClear={true}
                      format="DD/MM/YYYY"
                      value={dateRange}
                    />
                  </div>
                </div>

                <div className="average-period-navigation">
                  <Button
                    onClick={() => handlePeriodChange('previous')}
                    disabled={!hasSessions}
                  >
                    {t("stats_previous_period", "Previous period")}
                  </Button>
                  <span className="period-indicator">
                    {getPeriodLabel()}
                  </span>
                  <Button
                    onClick={() => handlePeriodChange('next')}
                    disabled={!hasSessions || isFuturePeriod()}
                  >
                    {t("stats_next_period", "Next period")}
                  </Button>
                </div>
              </div>

              <div className="average-summary-full-width">
                {averagesToDisplay ? (
                  <Descriptions
                    bordered
                    size="small"
                    column={1}
                    title={
                      dateRange[0] && dateRange[1]
                        ? t("stats_averages_for_range", {
                            start: dayjs(dateRange[0]).format("DD/MM/YY"),
                            end: dayjs(dateRange[1]).format("DD/MM/YY"),
                            defaultValue: "Averages ({{start}} - {{end}})",
                          })
                        : t("stats_global_averages", "Global averages")
                    }
                  >
                    <Descriptions.Item label={t("stats_nb_sessions", "Sessions")}>{averagesToDisplay.count}</Descriptions.Item>
                    <Descriptions.Item label={t("stats_avg_difficulty", "Average difficulty")}>{averagesToDisplay.difficulty}</Descriptions.Item>
                    <Descriptions.Item label={t("stats_avg_pain", "Average pain")}>{averagesToDisplay.pain}</Descriptions.Item>
                    <Descriptions.Item label={t("stats_avg_walking", "Average walking (min)")}>{averagesToDisplay.walking}</Descriptions.Item>
                    <Descriptions.Item label={t("stats_avg_exercises", "Average exercises")}>{averagesToDisplay.exercises}</Descriptions.Item>
                  </Descriptions>
                ) : (
                  <Alert
                    message={t(
                      "stats_no_session_in_selected_range",
                      "No session found in the selected date range."
                    )}
                    type="info"
                    showIcon
                  />
                )}
              </div>

              <div className="average-chart-full-width">
                {progressionDataToDisplay.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={progressionDataToDisplay} margin={{ top: 5, right: 20, bottom: 25, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(tick) => dayjs(tick).format('DD/MM')}
                        angle={-45}
                        textAnchor="end"
                        height={50}
                      />
                      <YAxis />
                      <Tooltip labelFormatter={(label) => dayjs(label).format('DD MMMM YYYY')} />
                      <Legend />
                      <Line type="monotone" dataKey="difficulty" stroke="#8884d8" name={t("text_average_difficulty", "Average Difficulty")} dot={false} />
                      <Line type="monotone" dataKey="pain" stroke="#82ca9d" name={t("text_average_pain", "Average Pain")} dot={false} />
                      <Line type="monotone" dataKey="walking" stroke="#0088FE" name={t("text_average_walking", "Average Walking")} dot={false} />
                      <Line type="monotone" dataKey="exercises" stroke="#ff7300" name={t("text_average_exercises", "Average Exercises")} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="chart-placeholder">
                    {t("stats_no_data_for_selected_period", "No data to display for the selected period.")}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Definition des PropTypes
PatientStats.propTypes = {
  patient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func,
  onExportCsvReady: PropTypes.func,
};



